import bugsnag from "@bugsnag/js";
import React, { FunctionComponent, useCallback, useEffect, useState } from "react";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import BaseSelectionProvider from "../../../../providers/selection/BaseSelectionProvider";
import BaseUploadProvider from "../../../../providers/upload/BaseUploadProvider";
import { SelectionPhoto } from "../../../../types/selection/selection";
import UploadOnboarding from "./UploadOnboarding";
import UploadSummary from "./UploadSummary";
import SelectionProviderBrowser from "../SelectionProviderBrowser";
import "./style.scss";

interface Props {
  maxSelectableItems?: number;
  onUploadQuantityChange: (quantity: number | undefined) => void;
  onFileChange: (files: File[]) => Promise<void>;
  onSelectPhoto: (selectionPhoto: SelectionPhoto) => void;
  pageKey?: string;
  providerInstance: BaseSelectionProvider;
  selectedPhotoKeys: Array<{ key: string; providerRef: string }>;
  selectionPhotos: SelectionPhoto[];
  uploadProvider?: BaseUploadProvider;
}

const UploadManager: FunctionComponent<Props> = ({
  maxSelectableItems,
  onUploadQuantityChange,
  onFileChange,
  onSelectPhoto,
  pageKey,
  providerInstance,
  selectedPhotoKeys,
  selectionPhotos,
  uploadProvider,
}) => {
  const [albumOpened, setAlbumOpened] = useState<boolean>(false);
  const [albumsInSummary, setAlbumsInSummary] = useState<string[]>([]);
  const [uploadingQuantity, setUploadingQuantity] = useState<number | undefined>();
  const [uploadVersion, setUploadVersion] = useState<number>(0);

  const { dispatch } = useCustomizationContext();

  useEffect(() => {
    if (!uploadProvider) {
      return;
    }

    return uploadProvider.onQueueEmpty(() => {
      dispatch({
        code: "KFV-002",
        type: "addNotificationByCode",
      });

      providerInstance.refreshAlbums();
      providerInstance.refreshAlbumPhotos();

      setUploadVersion((prevUploadVersion) => prevUploadVersion + 1);
      onUploadQuantityChange(undefined);
      setUploadingQuantity(undefined);
      setHasPhotosFromUpload(
        (hasPhotosFromUploadValue) => hasPhotosFromUploadValue || uploadProvider.finished.length > 0
      );
    });
  }, [dispatch, onUploadQuantityChange, setUploadVersion, uploadProvider, providerInstance]);

  const initOnFileChange = useCallback(
    (files: File[]) => {
      onUploadQuantityChange(files.length);
      setUploadingQuantity(files.length);
      setHasPhotosFromUpload(true);
      return onFileChange(files);
    },
    [onFileChange, onUploadQuantityChange]
  );

  const handleAlbumOpened = (album: string | null) => {
    setAlbumOpened(album !== null);
  };

  const selectionFromUpload = selectionPhotos.filter(
    (selectionPhoto) => selectionPhoto.provider === "upload"
  );

  const [hasPhotosFromUpload, setHasPhotosFromUpload] = useState<boolean>(
    uploadProvider
      ? uploadProvider.finished.length > 0 ||
          uploadProvider.queue.length > 0 ||
          uploadProvider.count() > 0 ||
          selectionFromUpload.length > 0
      : false
  );

  useEffect(() => {
    providerInstance
      .getAlbums()
      .then((albums) => {
        setHasPhotosFromUpload(
          (hasPhotosFromUploadValue) => hasPhotosFromUploadValue || albums.length > 0
        );
      })
      .catch((e: Error) => bugsnag.notify(e));
  }, [setHasPhotosFromUpload, providerInstance]);

  const handleAlbumDisplayed = (albumKey: string) => {
    setAlbumsInSummary((albumsList) => [...albumsList, albumKey]);
  };

  return (
    <div className="UploadManager">
      {providerInstance && (hasPhotosFromUpload || albumOpened) ? (
        <>
          {!albumOpened && (
            <UploadSummary
              loading={!!uploadingQuantity && uploadingQuantity > 0}
              maxSelectableItems={maxSelectableItems}
              onDisplayAlbumContent={handleAlbumDisplayed}
              onFileChange={initOnFileChange}
              onSelect={onSelectPhoto}
              providerInstance={providerInstance}
              selectedPhotoKeys={selectedPhotoKeys}
              selectionPhotos={selectionPhotos}
              uploadProvider={uploadProvider}
            />
          )}
          <SelectionProviderBrowser
            albumsToIgnore={albumsInSummary}
            onAlbumOpened={handleAlbumOpened}
            onSelect={onSelectPhoto}
            pageKey={pageKey}
            provider="upload"
            providerInstance={providerInstance}
            selectedPhotoKeys={selectedPhotoKeys}
            selectionPhotos={selectionPhotos}
            uploadVersion={uploadVersion}
          />
        </>
      ) : null}
      {!!uploadProvider && !albumOpened && !hasPhotosFromUpload ? (
        <UploadOnboarding onFileChange={initOnFileChange} />
      ) : null}
    </div>
  );
};

export default UploadManager;
